import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
    Sparkles, MessageCircle, AlertTriangle, TrendingUp, Users,
    Calendar, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
    visites: { bg: 'bg-blue-500/15', text: 'text-blue-300', border: 'border-blue-500/40' },
    prix: { bg: 'bg-amber-500/15', text: 'text-amber-300', border: 'border-amber-500/40' },
    timing: { bg: 'bg-purple-500/15', text: 'text-purple-300', border: 'border-purple-500/40' },
    exclusivité: { bg: 'bg-pink-500/15', text: 'text-pink-300', border: 'border-pink-500/40' },
    communication: { bg: 'bg-cyan-500/15', text: 'text-cyan-300', border: 'border-cyan-500/40' },
    marketing: { bg: 'bg-emerald-500/15', text: 'text-emerald-300', border: 'border-emerald-500/40' },
    négociation: { bg: 'bg-orange-500/15', text: 'text-orange-300', border: 'border-orange-500/40' },
    administratif: { bg: 'bg-slate-500/15', text: 'text-slate-300', border: 'border-slate-500/40' },
    autre: { bg: 'bg-white/[0.05]', text: 'text-white/60', border: 'border-white/15' },
};

function fmtRelDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
        if (diff < 1) return "il y a < 1 h";
        if (diff < 24) return `il y a ${diff} h`;
        const days = Math.floor(diff / 24);
        if (days === 1) return 'hier';
        if (days < 30) return `il y a ${days} j`;
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    } catch (_e) { return iso.slice(0, 10); }
}

export default function SuperAdminSellerVoiceTab({ token }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        axios.get(`/api/super-admin/seller-voice?days=${days}`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((r) => setData(r.data))
          .catch(() => toast.error('Erreur de chargement'))
          .finally(() => setLoading(false));
    }, [token, days]);

    const maxCatCount = useMemo(() => {
        if (!data?.by_category?.length) return 1;
        return Math.max(...data.by_category.map((c) => c.count));
    }, [data]);

    if (loading) return <div className="text-center py-12 text-white/50">Chargement…</div>;
    if (!data) return null;

    const summary = data.summary || {};

    return (
        <div className="space-y-6" data-testid="seller-voice-tab">
            {/* Intro */}
            <div className="rounded-lg bg-gradient-to-r from-[#681793]/10 to-[#FF914D]/10 border border-[#FF914D]/20 p-4">
                <p className="text-sm text-white/85">
                    <strong className="text-white">🎙️ La Voix du Vendeur</strong> — Insights sur ce que vos vendeurs demandent le plus à l'IA Confident. Permet d'<strong>adapter vos argumentaires</strong> et d'identifier les points de friction récurrents.
                </p>
            </div>

            {/* Period selector */}
            <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Période :</span>
                {[7, 30, 90].map((d) => (
                    <button
                        key={d}
                        type="button"
                        onClick={() => setDays(d)}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                            days === d
                                ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white'
                                : 'bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/[0.08] border border-white/10'
                        }`}
                        data-testid={`sv-period-${d}`}
                    >
                        {d} jours
                    </button>
                ))}
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard icon={MessageCircle} label="Questions" value={summary.total_questions || 0} color="text-blue-400" />
                <SummaryCard icon={Users} label="Vendeurs actifs" value={summary.unique_sellers || 0} color="text-purple-400" />
                <SummaryCard icon={AlertTriangle} label="Sujets sensibles" value={summary.sensitive_count || 0} color={summary.sensitive_count ? 'text-red-400' : 'text-white/40'} />
                <SummaryCard icon={Calendar} label="Période" value={`${summary.period_days} j`} color="text-orange-400" />
            </div>

            {/* No data state */}
            {summary.total_questions === 0 && (
                <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center">
                    <Sparkles className="w-10 h-10 mx-auto text-white/30 mb-3" />
                    <h3 className="text-white font-bold mb-1">Pas encore de questions</h3>
                    <p className="text-white/55 text-sm max-w-md mx-auto">
                        Quand vos vendeurs commenceront à utiliser le chat IA depuis leur espace, leurs questions apparaîtront ici avec catégorisation automatique.
                    </p>
                </div>
            )}

            {/* By category */}
            {summary.total_questions > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="w-4 h-4 text-[#FF914D]" />
                        <h3 className="text-white font-bold">Répartition par catégorie</h3>
                    </div>
                    <div className="space-y-2">
                        {data.by_category.map((c) => {
                            const meta = CATEGORY_COLORS[c.category] || CATEGORY_COLORS.autre;
                            const pct = Math.round((c.count / maxCatCount) * 100);
                            return (
                                <div key={c.category} className="flex items-center gap-3" data-testid={`sv-cat-${c.category}`}>
                                    <span className={`text-xs font-bold uppercase tracking-wider w-32 ${meta.text}`}>{c.category}</span>
                                    <div className="flex-1 h-7 rounded-md overflow-hidden bg-white/[0.04] relative">
                                        <div className={`h-full ${meta.bg} ${meta.border} border-r-2 transition-all duration-500`} style={{ width: `${pct}%` }} />
                                        <div className="absolute inset-0 flex items-center px-3 text-xs font-bold text-white">
                                            {c.count} <span className="text-white/40 ml-1">({c.percentage}%)</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Top questions */}
            {data.top_questions?.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-4 h-4 text-[#FF914D]" />
                        <h3 className="text-white font-bold">Top questions des vendeurs</h3>
                    </div>
                    <div className="space-y-2">
                        {data.top_questions.map((q, i) => {
                            const meta = CATEGORY_COLORS[q.category] || CATEGORY_COLORS.autre;
                            return (
                                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5" data-testid={`sv-question-${i}`}>
                                    <span className="text-xs font-bold text-white/30 mt-0.5 w-6 shrink-0">{i + 1}.</span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-white/90">{q.text}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`text-[10px] uppercase tracking-wider font-bold ${meta.text} px-2 py-0.5 rounded-full ${meta.bg} border ${meta.border}`}>{q.category}</span>
                                            <span className="text-[10px] text-white/40">{q.count} fois</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Top sellers */}
            {data.top_sellers?.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <Users className="w-4 h-4 text-[#FF914D]" />
                        <h3 className="text-white font-bold">Vendeurs les plus actifs</h3>
                    </div>
                    <div className="space-y-1">
                        {data.top_sellers.map((s, i) => (
                            <div key={s.seller_id} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-white/[0.04]" data-testid={`sv-seller-${i}`}>
                                <span className="text-xs font-bold text-white/30 w-6 shrink-0">{i + 1}.</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-white truncate">
                                        {s.first_name} {s.last_name}
                                        <span className="text-white/40 text-xs ml-2">· {s.email}</span>
                                    </p>
                                </div>
                                <span className="text-xs text-white/40 mr-3">{fmtRelDate(s.last_question_at)}</span>
                                <span className="text-sm font-black text-[#FF914D]">{s.question_count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Sensitive escalations */}
            {data.recent_sensitive?.length > 0 && (
                <div className="rounded-2xl border border-red-500/30 bg-red-500/[0.05] p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        <h3 className="text-white font-bold">Sujets sensibles récents</h3>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-red-400 bg-red-500/15 border border-red-500/30 px-2 py-0.5 rounded-full">À surveiller</span>
                    </div>
                    <div className="space-y-2">
                        {data.recent_sensitive.map((m, i) => (
                            <div key={i} className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                                <p className="text-sm text-white/85 mb-1">{m.content}</p>
                                <div className="flex items-center gap-2 text-[10px] text-white/40">
                                    <span className="uppercase font-bold text-red-400">{m.topic || 'sensible'}</span>
                                    <span>·</span>
                                    <span>{fmtRelDate(m.created_at)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value, color }) {
    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`sv-card-${label}`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-3.5 h-3.5 ${color}`} />
                <span className="text-[10px] uppercase tracking-wider font-bold text-white/60">{label}</span>
            </div>
            <div className="text-2xl font-black text-white">{value}</div>
        </div>
    );
}
